// src/components/management/Calendar.jsx
import React, { useEffect, useState } from "react";
import {
  format,
  addDays,
  parseISO,
  differenceInCalendarDays,
  isValid,
  subDays,
} from "date-fns";
import "../../styles/components/management/Calendar/Calendar.css";
import BookingDetails from "./Calendar/BookingDetails";

const API_BASE = process.env.REACT_APP_API_BASE;

// map PLATFORM → icon path (in your public folder)
const PLATFORM_ICONS = {
  "Airbnb": "/icons/airbnb.png",
  "Booking.com": "/icons/bookingcom.png",
  "zan.investments.bnb": "/logo.png",
};

const Calendar = () => {
  const [properties, setProperties] = useState([]);
  const [units, setUnits] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedProperty, setSelectedProperty] = useState("");
  const [startDate, setStartDate] = useState(() =>
    format(new Date(), "yyyy-MM-dd")
  );
  const [dates, setDates] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);

  // 1) load properties
  useEffect(() => {
    fetch(`${API_BASE}/api/properties`)
      .then((r) => r.json())
      .then((data) => setProperties(data.properties || data || []))
      .catch(console.error);
  }, []);

  // 2) build 14-day window
  useEffect(() => {
    const sd = parseISO(startDate);
    if (!isValid(sd)) return;
    const arr = Array.from({ length: 14 }, (_, i) =>
      format(addDays(sd, i), "yyyy-MM-dd")
    );
    setDates(arr);
  }, [startDate]);

  // 3) on property change: fetch units→rooms→bookings (with guests)
  useEffect(() => {
    if (!selectedProperty || selectedProperty === "test") return;

    const winStart = parseISO(startDate);
    const winEnd = parseISO(dates[dates.length - 1] || startDate);
    const winStartStr = isValid(winStart) ? format(winStart, "yyyy-MM-dd") : "";
    const winEndStr = isValid(winEnd) ? format(winEnd, "yyyy-MM-dd") : "";

    fetch(
      `${API_BASE}/api/properties/units?propertyId=${selectedProperty}&includeRooms=true`
    )
      .then((r) => r.json())
      .then(async (data) => {
        const fetched = Array.isArray(data) ? data : data.units || [];
        setUnits(fetched);

        const all = [];
        for (const unit of fetched) {
          for (const room of unit.rooms || []) {
            const url =
              `${API_BASE}/api/bookings?ROOM_ID=${room.ROOM_ID}` +
              (winStartStr ? `&departureFrom=${winStartStr}` : "") +
              (winEndStr ? `&arrivalTo=${winEndStr}` : "") +
              `&includeGuests=true`;
            console.log("Fetching:", url);
            try {
              const resp = await fetch(url);
              const items = await resp.json();
              items.forEach((b) => all.push(b));
            } catch (e) {
              console.error("Fetch error:", e);
            }
          }
        }

        setBookings(all);
        setStatuses([...new Set(all.map((b) => b.STATUS))]);
      })
      .catch(console.error);
  }, [selectedProperty, dates]);

  // render each booking bar
  const renderBookingBar = (b) => {
    if (statusFilter !== "all" && b.STATUS !== statusFilter) return null;

    const winStart = parseISO(startDate);
    const winEnd = parseISO(dates[dates.length - 1] || startDate);
    if (!isValid(winStart) || !isValid(winEnd)) return null;

    const arr = parseISO(b.ARRIVAL),
      dep = parseISO(b.DEPARTURE);
    if (dep < winStart || arr > winEnd) return null;

    // clip to window
    const segStart = arr < winStart ? winStart : arr;
    const segEnd = dep > winEnd ? winEnd : dep;
    const offset = differenceInCalendarDays(segStart, winStart);
    const span = differenceInCalendarDays(segEnd, winStart);

    const leftPct = (offset + 0.5) * (100 / dates.length);
    const widthPct = (span - offset) * (100 / dates.length);

    const iconSrc = PLATFORM_ICONS[b.PLATFORM] || PLATFORM_ICONS["zan.investments.bnb"];
    const ref = b.API_REFERENCE || b.ID;
    const name = b.guest
      ? `${b.guest.FIRST_NAME} ${b.guest.LAST_NAME}`
      : "";

    return (
      <div
        key={b.ID}
        className="booking-box"
        style={{
          left: `${leftPct}%`,
          width: `${widthPct}%`,
          backgroundColor:
            { confirmed: "#4caf50", new: "#2196f3", cancelled: "#f44336" }[b.STATUS] ||
            "#999",
        }}
        onClick={() => setSelectedBooking(b)}
      >
        <img
          src={iconSrc}
          alt={b.PLATFORM}
          className="platform-icon"
        />
        <div className="booking-info">
          <div className="booking-header">
            {ref} – {name}
          </div>
          <div className="booking-dates">
            {format(arr, "dd/MM")} → {format(dep, "dd/MM")}
          </div>
        </div>
      </div>
    );
  };

  // render each room row
  const renderRoomRow = (room) => {
    const roomBookings = bookings.filter((x) => x.ROOM_ID === room.ROOM_ID);
    return (
      <tr key={room.ROOM_ID}>
        <td className="room-name-cell">{room.ROOM_NAME}</td>
        <td className="booking-cell-wrapper" colSpan={dates.length}>
          <div className="booking-row-container">
            {roomBookings.map(renderBookingBar)}
          </div>
        </td>
      </tr>
    );
  };

  const selectedProp = properties.find(
    (p) => p.PROPERTY_ID === selectedProperty
  );
  const propImg = selectedProp?.PROPERTY_IMAGE;

  return (
    <div className="calendar-view">
      <div className="calendar-controls">
        <button
          onClick={() =>
            setStartDate(format(subDays(parseISO(startDate), 14), "yyyy-MM-dd"))
          }
        >
          ◀ Prev
        </button>
        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            const val = e.target.value;
            const dt = parseISO(val);
            if (isValid(dt)) setStartDate(val);
          }}
        />
        <button
          onClick={() =>
            setStartDate(format(addDays(parseISO(startDate), 14), "yyyy-MM-dd"))
          }
        >
          Next ▶
        </button>

        <select
          value={selectedProperty}
          onChange={(e) => setSelectedProperty(e.target.value)}
        >
          <option value="">Select Property</option>
          <option value="all">All</option>
          {properties.map((p) => (
            <option key={p.PROPERTY_ID} value={p.PROPERTY_ID}>
              {p.PROPERTY_NAME}
            </option>
          ))}
        </select>

        {propImg && (
          <img
            src={propImg}
            alt=""
            style={{
              height: 24,
              marginLeft: 8,
              borderRadius: 4,
            }}
          />
        )}

        {statuses.length > 0 && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">Show All</option>
            {statuses.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="table-responsive">
        <table className="calendar-table">
          <thead>
            <tr>
              <th>Room</th>
              {dates.map((d) => (
                <th key={d}>{format(parseISO(d), "dd/MM")}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {units.map((unit) => (
              <React.Fragment key={unit.UNIT_ID}>
                <tr className="unit-group-row">
                  <td colSpan={dates.length + 1}>
                    {unit.UNIT_NAME} — {unit.UNIT_ID}
                  </td>
                </tr>
                {unit.rooms.map(renderRoomRow)}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {selectedBooking && (
        <BookingDetails
          booking={selectedBooking}
          property={selectedProp}
          onClose={() => setSelectedBooking(null)}
        />
      )}
    </div>
  );
};

export default Calendar;
